import React from "react";
import ikigaiSeed from "./data/ikigaiSeed";

export default function PDIKIGAIHelp() {
  const copyTask = async (task) => {
    const text = `🦋 PurpleDoor IKIGAI BOS™

Day ${task.day}
📅 ${task.date}

🎯 ${task.todoTitle}

Objective: ${task.objective}
Tags: ${task.tags.join(", ")}`;

    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="rounded-xl border border-purple-500/30 bg-gradient-to-br from-purple-950 via-gray-900 to-black p-2 shadow-lg">
        <h2 className="text-sm lg:text-lg font-bold text-purple-300">
          🦋 Full 90-Day PurpleDoor IKIGAI BOS
        </h2>

        <p className="mt-2 text-[10px] lg:text-xs text-gray-200">
          A Purpose Discovery, Skill Building, Contribution & Meaningful Impact
          Operating System designed to help individuals find direction, create
          value and evolve continuously through repeatable 90-day cycles.
        </p>
      </div>

      {/* Phases */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
        <div className="rounded-xl border border-purple-500/20 bg-gray-900 p-2">
          <h3 className="text-xs lg:text-sm font-semibold text-purple-300 mb-1">
            🤔 Phase 1 (Days 1-30)
          </h3>

          <div className="space-y-2 text-[10px] lg:text-xs text-gray-100">
            <div>🤔 Self Awareness</div>
            <div>❤️ Energy Discovery</div>
            <div>🌱 Strengths</div>
            <div>💡 Curiosity</div>
            <div>🎯 Purpose Discovery</div>
          </div>
        </div>

        <div className="rounded-xl border border-purple-500/20 bg-gray-900 p-2">
          <h3 className="text-xs lg:text-sm font-semibold text-purple-300 mb-1">
            🚀 Phase 2 (Days 31-60)
          </h3>

          <div className="space-y-2 text-[10px] lg:text-xs text-gray-100">
            <div>📚 Learn</div>
            <div>🛠 Build</div>
            <div>🤝 Contribute</div>
            <div>🚀 Create</div>
            <div>📈 Momentum</div>
          </div>
        </div>

        <div className="rounded-xl border border-purple-500/20 bg-gray-900 p-2">
          <h3 className="text-xs lg:text-sm font-semibold text-purple-300 mb-1">
            🦋 Phase 3 (Days 61-90)
          </h3>

          <div className="space-y-2 text-[10px] lg:text-xs text-gray-100">
            <div>🧠 Integrate</div>
            <div>🌟 Confidence</div>
            <div>📈 Impact</div>
            <div>🚀 Expand</div>
            <div>🦋 Renew</div>
          </div>
        </div>
      </div>

      {/* Transformation */}
      <div className="rounded-xl border border-green-500/20 bg-green-950/20 p-2">
        <h3 className="text-xs lg:text-sm font-semibold text-green-300 mb-1">
          🦋 Expected Transformation
        </h3>

        <div className="flex flex-wrap items-center gap-2 text-[10px] lg:text-xs text-gray-50">
          <span>🤔 Confused</span>
          <span>⬇️</span>

          <span>🔍 Curious</span>
          <span>⬇️</span>

          <span>🧭 Exploring</span>
          <span>⬇️</span>

          <span>🎯 Purposeful</span>
          <span>⬇️</span>

          <span>🚀 Creating</span>
          <span>⬇️</span>

          <span>🌟 Impacting</span>
          <span>⬇️</span>

          <span>🦋 Evolving</span>
        </div>
      </div>

      {/* Philosophy */}
      <div className="rounded-xl border border-purple-500/20 bg-purple-950/10 p-2">
        <h3 className="text-xs lg:text-sm font-semibold text-purple-300 mb-2">
          💎 PurpleDoor IKIGAI Philosophy
        </h3>

        <p className="text-[10px] lg:text-xs text-gray-200">
          IKIGAI is not a one-time discovery. It is a living, evolving purpose.
          Every 90 days you discover, build, contribute, reflect and renew. The
          outcome is not perfection. The outcome is continuous meaningful
          growth.
        </p>
      </div>

      {/* Tasks */}
      <div className="rounded-xl border border-gray-700 bg-gray-900 overflow-hidden">
        <div className="sticky top-0 z-10 bg-gray-900 border-b border-gray-700 px-4 py-2">
          <h3 className="text-xs lg:text-sm font-semibold text-purple-300">
            🦋 Guided 90-Day IKIGAI Activities
          </h3>

          <p className="text-[10px] lg:text-xs text-gray-200 mt-1">
            📋 Copy a daily IKIGAI mission and paste it directly into your
            PurpleDoor Daily Task Input.
          </p>
        </div>

        <div className="py-2">
          {ikigaiSeed.map((task) => (
            <div
              key={task.day}
              className="border-b border-gray-800 p-3 hover:bg-purple-500/5 transition"
            >
              <div className="flex justify-between gap-3">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-purple-300 text-[10px] lg:text-xs font-semibold">
                      Day {task.day}
                    </span>

                    <span className="text-[10px] lg:text-xs text-gray-100">{task.date}</span>
                  </div>

                  <div className="mt-1 text-[10px] lg:text-xs text-white font-medium">
                    {task.todoTitle}
                  </div>

                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className="px-2 py-1 rounded-full text-[10px] lg:text-xs bg-purple-900 text-purple-200 border border-purple-700">
                      🎯 {task.objective}
                    </span>

                    {task.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-1 rounded-full text-[10px] lg:text-xs bg-gray-800 text-gray-200 border border-gray-700"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>

                <button
                  onClick={() => copyTask(task)}
                  className="w-fit px-1 py-1 rounded-lg text-white text-xs transition"
                >
                  📋
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
